import { createContext, Dispatch, SetStateAction, useContext, useEffect, useState } from 'react';
import classes from './NoticeBar.module.scss';
import { FCC } from '../../../lib/types';
import clsx from 'clsx';
import { Alert24, Check24, Info24 } from '@bphxd/ds-core-react/lib/icons';
import { differenceInMilliseconds, isFuture } from 'date-fns';

type TNotice = { level: 'success' | 'error' | 'info'; text: string; expiresAt?: Date };

const NoticeBarContext = createContext<{
  notice: TNotice | null;
  setNotice: Dispatch<SetStateAction<TNotice | null>>;
}>({ notice: null, setNotice: () => null });

export const NoticeBarProvider: FCC<{ value?: TNotice }> = ({ children, value }) => {
  const [notice, setNotice] = useState<TNotice | null>(value || null);

  const hasNotExpired = isFuture(notice?.expiresAt || new Date(9999, 9, 9));
  useEffect(() => {
    if (notice?.expiresAt && hasNotExpired) {
      const duration = differenceInMilliseconds(notice?.expiresAt, Date.now());
      const timeoutRef = setTimeout(() => {
        setNotice(null);
      }, duration);

      return () => clearTimeout(timeoutRef);
    }
  }, [notice?.expiresAt]);

  return (
    <div className={classes.root}>
      {notice && hasNotExpired && (
        <div className={clsx(classes.notice, classes[notice.level])}>
          {notice.level === 'success' ? (
            <Check24 />
          ) : notice.level === 'error' ? (
            <Alert24 />
          ) : notice.level === 'info' ? (
            <Info24 />
          ) : null}
          <span>{notice.text}</span>
        </div>
      )}
      <NoticeBarContext.Provider value={{ notice, setNotice }}>
        {children}
      </NoticeBarContext.Provider>
    </div>
  );
};

export const useNoticeBar = () => {
  const { setNotice } = useContext(NoticeBarContext);

  const sendError = (text: string, expireAfter = 5000) => {
    const notice: TNotice = { level: 'error', text };
    if (expireAfter !== Infinity) {
      notice.expiresAt = new Date(Date.now() + expireAfter);
    }
    setNotice(notice);
  };

  const sendSuccess = (text: string, expireAfter = 10000) => {
    const notice: TNotice = { level: 'success', text };
    if (expireAfter !== Infinity) {
      notice.expiresAt = new Date(Date.now() + expireAfter);
    }
    setNotice(notice);
  };

  const sendInfo = (text: string, expireAfter = Infinity) => {
    const notice: TNotice = { level: 'info', text };
    if (expireAfter !== Infinity) {
      notice.expiresAt = new Date(Date.now() + expireAfter);
    }
    setNotice(notice);
  };

  const clear = () => setNotice(null);

  return { sendError, sendSuccess, sendInfo, clear };
};

export default NoticeBarProvider;
